import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { LogIn } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { login } from '@/api/auth-api'
import { useAuth } from '@/auth/use-auth'
import { ProblemAlert } from '@/components/error-surface'
import { PasswordField, TextField } from '@/components/form/fields'
import { Button } from '@/components/ui/button'

import { AuthLayout } from './auth-layout'

const loginSchema = z.object({
  username: z.string().min(1, 'Enter your username.'),
  password: z.string().min(1, 'Enter your password.'),
})

type LoginValues = z.infer<typeof loginSchema>

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/login' })

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  })

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (outcome, variables) => {
      auth.applyOutcome(outcome, variables.username)

      switch (outcome.outcome) {
        case 'authenticated':
          // The guard stashed where the user was heading. It has already been validated as an
          // in-application path — see auth-search.ts — so it is safe to navigate to verbatim.
          void (search.redirect ? navigate({ href: search.redirect }) : navigate({ to: '/' }))
          break
        case 'mfa_required':
          void navigate({ to: '/mfa', search })
          break
        case 'mfa_enrolment_required':
          void navigate({ to: '/mfa/enrol', search })
          break
        case 'password_change_required':
          void navigate({ to: '/password/change', search })
          break
        default:
          // Exhaustiveness: a new sign-in outcome must fail this build, not silently do
          // nothing. `mfa_enrolment_required` went unhandled here for weeks — the server
          // answered correctly and the operator watched a motionless form.
          outcome satisfies never
      }
    },
  })

  return (
    <AuthLayout title="Sign in" description="Use your staff username. Shared accounts are not permitted.">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          void form.handleSubmit((values) => {
            mutation.mutate(values)
          })(event)
        }}
        noValidate
      >
        <TextField
          control={form.control}
          name="username"
          label="Username"
          autoComplete="username"
          autoFocus
          required
        />

        <PasswordField
          control={form.control}
          name="password"
          label="Password"
          autoComplete="current-password"
          required
        />

        {mutation.isError ? <ProblemAlert error={mutation.error} /> : null}

        <Button type="submit" variant="primary" className="w-full" disabled={mutation.isPending}>
          <LogIn />
          {mutation.isPending ? 'Signing in…' : 'Sign in'}
        </Button>

        <p className="text-2xs text-muted">
          A lost password is reset by an administrator under dual control — there is no self-service
          reset, and support will never ask you for your password or a one-time code.
        </p>
      </form>
    </AuthLayout>
  )
}
